import { includes } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import roles from 'common/constants/roles';
import useIdleSignOut from 'common/hooks/useIdleSignOut';
import { WebSocketProvider } from 'common/hooks/useWebSocket';

const ProtectedRoutes = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const userRoles = useSelector(({ auth }) => auth.currentTenantRoles);
  const { username, userId } = useSelector(
    ({ auth }) => ({ username: auth.username, userId: auth.userId }),
    shallowEqual
  );

  useEffect(() => {
    // Don't run role-based redirects for unauthenticated users — the synchronous
    // <Navigate to="/signin"> render below handles that case.
    if (!username || !userId) return;

    const hasNoRoles = !userRoles?.length;
    const hasShippingRole = includes(userRoles, roles.shippingUser);
    const hasNonShippingRole = userRoles?.some((role) => role !== roles.shippingUser);

    if (hasNoRoles && location.pathname !== '/request-access') {
      navigate('/request-access');
      return;
    }

    // Redirect only pure shipping users (no other roles) to Shipments on root.
    if (hasShippingRole && !hasNonShippingRole && location.pathname === '/') {
      navigate('/shipments');
    }
  }, [userRoles, navigate, location.pathname, username, userId]);

  useIdleSignOut();

  if (!username || !userId) {
    return (
      <Navigate to='/signin' state={{ from: `${location.pathname}${location.search}` }} replace />
    );
  }

  return (
    <WebSocketProvider>
      <div key={username}>{children}</div>
    </WebSocketProvider>
  );
};

ProtectedRoutes.propTypes = {
  children: PropTypes.element.isRequired,
};

export default ProtectedRoutes;
